import { useEffect, useState } from 'react';
import { Storage } from '../lib/storage';
import { playerIdentityMode } from '../lib/api/customerContext';
import { fetchMyPlayer, type MyPlayerView } from '../lib/api/myPlayer';
import { memberSession } from '../lib/auth';

// ─────────────────────────────────────────────────────────────────────
// useCurrentMember — WHO is using the app right now.
//
// TWO SOURCES, and which one applies is decided by the surface (#393).
//
// In MEMBER MODE the answer is the session's own member — the app signed in as
// a specific person, and `demo_user_v1` is a CLUB key a member may not read.
// Reading it answered 401 and ended the session; falling through to the club
// resolution below would be worse still, because the roster loaded from
// `/member/bootstrap` is everyone, so «first player» would show a member as
// somebody else.
//
// On the CLUB surface there are no per-user accounts: the app logs in at club
// level, and the "logged in as" player is a single player id kept under
// `demo_user_v1`. That
// key used to be read and written by hand in two places (ActivitiesView's
// profile switcher and BookingEditModal's booker prefill). This hook is the one
// shared home for it, and is the identity the mobile app builds on — when real
// accounts arrive, only this hook's resolution needs to change.
//
// Resolution order mirrors ActivitiesView exactly, so both surfaces agree on
// who «you» are: explicit pick → favourite player → first player.
// ─────────────────────────────────────────────────────────────────────

export const CURRENT_MEMBER_KEY = 'demo_user_v1';

/**
 * Pure: resolve the member from the player list + a (possibly stale) picked id.
 *
 * `inMemberMode` IS NOT A REFINEMENT — it changes what «not found» means.
 *
 * On the club surface the fallbacks are the product: nobody has picked a profile
 * yet, so the favourite or the first player is a reasonable «you».
 *
 * In the MEMBER app they are a cross-member identity leak. The session names one
 * person; if that person is not in the list, the honest answer is NOBODY. This
 * file's own header already said so — «the roster from `/member/bootstrap` is
 * everyone, so «first player» would show a member as somebody else» — and it was
 * right about the danger while the code fell through anyway: a member who signed
 * in was greeted by name as another member, with that member's level and rating
 * on the screen.
 */
export function resolveMember(
  effectivePlayerDB: any[] = [],
  memberId: any = null,
  identityIsProven = false,
): any | null {
  const db = effectivePlayerDB || [];
  const picked = memberId != null && db.find((p: any) => String(p.id) === String(memberId));
  if (picked) return picked;
  // THE THIRD ARGUMENT WAS `inMemberMode`, AND THE PROPERTY WAS NEVER THE
  // CREDENTIAL (#791). It is «somebody proved who this is», and the fallbacks
  // are a cross-identity leak for every proven identity, not only for a legacy
  // member session: an account signed in through `/auth/otp` whose player is
  // missing from the roster must be NOBODY here, exactly as a member is.
  if (identityIsProven) return null;
  return db.find((p: any) => p.isFavorite) || db[0] || null;
}

export function useCurrentMember(effectivePlayerDB: any[] = [], memberIdForList: any = null) {
  const [memberId, setMemberIdState] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);
  const [identityFailed, setIdentityFailed] = useState(false);
  /**
   * The ACCOUNT's own player, as `/me/player` describes it — or null.
   *
   * ── THE PERSON IS NOT A ROW IN A CLUB'S ROSTER ────────────────────
   *
   * This answer was used only for its id, which was then looked up in the
   * active club's roster. So with no club selected the lookup found nobody, and
   * every personal screen — the greeting, the level card, the rating — went
   * blank for a person the server had just named. Personal identity is the
   * account's; a roster row is a fact about one club. The owner's player-first
   * shell requires personal features to work with no club selected, so the
   * account's own player is KEPT and stands in when no roster row answers.
   *
   * Only for an account. A legacy member session's identity is its own club's
   * roster row by construction, and that path is untouched.
   */
  const [self, setSelf] = useState<MyPlayerView | null>(null);
  /**
   * Bumped by `retryIdentity()`, and in the effect's dependencies.
   *
   * THE RETRY #800 ASKS FOR. `identityFailed` said «could not ask» and the
   * surface had no way to ask again: the effect's only dependency was
   * `memberIdForList`, so a proven person whose identity read failed stayed
   * nobody until a remount. The fact without the remedy is half an answer.
   */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The member app already knows who it signed in as.
      //
      // TWO IDS NAME THE SAME PERSON, AND ONLY ONE OF THEM MATCHES THIS LIST.
      // The session carries the database UUID — what every member session
      // names — while every row in `effectivePlayerDB` carries the LEGACY id
      // (`9`, `km_custom_…`), because that is what the projections hand the
      // browser. So the lookup missed, the resolution fell through, and a signed
      // in member was shown as whoever happened to be first in the roster.
      //
      // `memberIdForList` prefers the id the LIST is keyed by, which the
      // bootstrap supplies as `me.id`; the session's uuid remains the identity
      // everything server-side is keyed on and is not used for this.
      // ONE DECISION, TAKEN IN ONE PLACE (#791 review). This branch used to ask
      // `memberModeActive()` on its own, which ignores the surface — so in the
      // club portal with a member session also live it took the MEMBER's
      // identity for a surface whose credential is the account's, and
      // disagreed with the predicate guarding the picker.
      const mode = playerIdentityMode();
      if (mode === 'member-session') {
        const id = memberIdForList ?? memberSession.state().member?.id ?? null;
        if (!cancelled) {
          if (id) setMemberIdState(id);
          setLoaded(true);
        }
        return;
      }
      // ── AN ACCOUNT ON THE PLAYER SURFACE (#791) ────────────────────
      //
      // The same answer from the other credential family: the server knows
      // which `Player` this account IS (`players.user_id`), and `GET
      // /me/player` returns it keyed by the LEGACY id — the id this list is
      // keyed by, which is why that endpoint projects `MemberSelf` rather than
      // the database uuid.
      //
      // NOT A PICK, AND NOT A FALLBACK WHEN IT FAILS. A read that does not
      // answer leaves the id null and the identity still PROVEN, so the
      // workspace shows nobody and asks again — it must never offer to choose
      // a player instead, which is the escalation this gate exists to stop.
      if (mode === 'account') {
        const { player, failed } = await fetchMyPlayer();
        if (!cancelled) {
          setSelf(player);
          if (player) setMemberIdState(player.id);
          // CARRIED, NOT DISCARDED (#791 review). The client distinguishes «no
          // player» from «could not ask», and the surface needs the second to
          // offer a retry rather than leaving somebody as nobody for ever. The
          // retry UI belongs with the navigation slice; losing the fact here
          // would make it unbuildable.
          setIdentityFailed(failed);
          setLoaded(true);
        }
        return;
      }
      try {
        const res = await Storage.get(CURRENT_MEMBER_KEY);
        if (!cancelled && res?.value) setMemberIdState(JSON.parse(res.value));
      } catch {
        /* no pick stored yet — fall through to the favourite / first player */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `memberIdForList` arrives with the bootstrap, a moment after mount — so it
    // is a dependency rather than a mount-once read, or the member app would
    // resolve once against nothing and keep that answer. `attempt` is the
    // explicit retry (#800).
  }, [memberIdForList, attempt]);

  // Storage-first: persist before the state flips, so a reload never shows a
  // profile that was never actually saved.
  const setMemberId = async (id: any) => {
    // A PROVEN IDENTITY CANNOT BE SWITCHED (#393, widened by #791).
    //
    // A member could not, because the write is a club key and their identity
    // is the session's — «changing it is a sign-out, not a profile switch».
    // That reasoning was never about the credential: an account that proved an
    // address holds exactly the same kind of identity, and letting it assert an
    // arbitrary player is the one thing the account session exists to stop.
    if (playerIdentityMode() !== 'picked') return;
    await Storage.set(CURRENT_MEMBER_KEY, JSON.stringify(id));
    setMemberIdState(id);
  };

  const proven = playerIdentityMode() !== 'picked';
  // THE CLUB'S ROW WHEN THERE IS ONE, THE ACCOUNT'S OWN PLAYER OTHERWISE.
  // In a club, the roster row carries what only that club knows and is the
  // right answer; with no club — or a club whose roster does not list this
  // person — the account's player keeps the personal screens honest instead of
  // empty. `self` is only ever set on the account branch above, so the picked
  // identity of the club portal and a legacy member session never reach it.
  // Whose account it is, is the store's key (`Padel24App`): another account
  // gets a new hook, never this one's answer.
  const member = resolveMember(effectivePlayerDB, memberId, proven) ?? self;
  // `proven` travels with the identity so the surface can tell «nobody picked
  // yet» from «this person is who the server says they are», which is what
  // decides whether a picker may be shown at all. `identityFailed` says the
  // second case is unanswered rather than empty — a retry, never a pick.
  return {
    member,
    memberId,
    setMemberId,
    loaded,
    proven,
    identityFailed,
    /** Ask again who this is. For «could not ask», never for «nobody». */
    retryIdentity: () => {
      setIdentityFailed(false);
      setLoaded(false);
      setAttempt((a) => a + 1);
    },
  };
}

/**
 * May the surface ask WHO YOU ARE? (#791)
 *
 * The picker is forced where it appears — `dismissible={!mustPick}` — so this
 * is not a presentation detail: it decides whether a person is required to
 * assert an identity before the app will do anything.
 *
 * A RULE RATHER THAN AN EXPRESSION, because the expression lived inline in
 * `MobileApp` and the gate has to be testable without rendering a screen with
 * forty props. `identity-gate.test.ts` drives it directly and asserts that
 * `MobileApp` still asks it rather than deciding for itself.
 *
 * `proven` is the load-bearing term, and it is false exactly where picking is
 * the honest answer: the club portal, where a club logs in at club level and
 * the person says which player they are, and the memory backend, where there
 * is no server to have proved anything.
 */
export function mustPickIdentity(state: {
  loaded: boolean;
  proven: boolean;
  memberId: unknown;
  playerCount: number;
}): boolean {
  return state.loaded && !state.proven && state.memberId == null && state.playerCount > 0;
}
